import {
  Context,
  createContext,
  forwardRef,
  useCallback,
  useContext,
} from 'react';

import { Flex, type FlexProps } from '@frontend/legacy-components';

import {
  TableContext,
  type BuiltContextValue,
  type BaseTableContextValue,
  type Cell,
} from './Table';

export const DEFAULT_CONTEXT = {
  registerCell: () => null,
};

type RowContextValue = {
  registerCell?: (cell: Cell) => void;
  setCellData?: (cell: Cell, column: number) => void;
  handleKeyDown?: (
    event: React.KeyboardEvent<HTMLDivElement>,
    column?: number,
  ) => boolean | undefined;
};

export const RowContext = createContext<RowContextValue>(DEFAULT_CONTEXT);

export interface RowProps extends Omit<FlexProps, 'role'> {
  role?: React.AriaRole | null;
  index?: number;
}

// A wrapper around the Flex component that provides a context for the cells in the row
export const Row = forwardRef<HTMLDivElement, RowProps>(
  ({ index, role = 'row', ...restProps }, ref) => {
    const { setCellData, handleKeyDown } = useContext<
      BuiltContextValue<BaseTableContextValue>
    >(
      // NB: Unfortunately, context in this module is dynamically modified, it's a bit of a spaghetti mess,
      // so instead we do the unknown as Context cast, because functionally these parts of context are necessary
      // for this component's functionality
      TableContext as unknown as Context<
        BuiltContextValue<BaseTableContextValue>
      >,
    );

    const onSetCellData = useCallback(
      (cell: Cell, column: number) => {
        if (index != null && column != null) {
          setCellData({ row: index, column, ...cell });
        }
      },
      [index, setCellData],
    );

    const onhandleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>, column?: number) => {
        if (index != null && column != null) {
          return handleKeyDown({ row: index, column, event });
        }
      },
      [handleKeyDown, index],
    );

    return (
      <RowContext.Provider
        value={{ setCellData: onSetCellData, handleKeyDown: onhandleKeyDown }}
      >
        <Flex
          ref={ref}
          role={role !== null ? role : undefined}
          flex={1}
          minW="min-content"
          h="min-content"
          minH={5}
          borderTop="1px solid"
          borderColor="separator.divider"
          {...restProps}
        />
      </RowContext.Provider>
    );
  },
);
Row.displayName = 'Row';

export default Row;
